/**
 * Panel de Gestión de Suscripciones - C&M Publicaciones
 * Vista para admins: talleres por vencer, suspendidos, cron manual
 */

import React, { useState, useEffect } from 'react';
import { suscripcionesAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  Calendar, AlertTriangle, CheckCircle, Clock, Users, 
  RefreshCw, Loader2, Play, XCircle, ArrowRight, Zap,
  MoreVertical, DollarSign, RotateCw, Send, Trophy, Hourglass,
} from 'lucide-react';
import NotificacionesMasivas from '../../components/admin/NotificacionesMasivas';
import RankingClientes from '../../components/admin/RankingClientes';

const AdminSuscripciones = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTabFromUrl = searchParams.get('activeTab') || 'por-vencer';
  const [activeTab, setActiveTab] = useState(activeTabFromUrl);

  useEffect(() => {
    if (activeTabFromUrl !== activeTab) {
      setActiveTab(activeTabFromUrl);
    }
  }, [activeTabFromUrl, activeTab]);

  const handleTabChange = (val) => {
    setActiveTab(val);
    const np = new URLSearchParams(searchParams);
    if (val === 'por-vencer') {
      np.delete('activeTab');
    } else {
      np.set('activeTab', val);
    }
    setSearchParams(np, { replace: true });
  };

  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [talleresVencer, setTalleresVencer] = useState([]);
  const [talleresEnGracia, setTalleresEnGracia] = useState([]);
  const [talleresSuspendidos, setTalleresSuspendidos] = useState([]);
  const [diasFiltro, setDiasFiltro] = useState(7);
  const [ejecutandoCron, setEjecutandoCron] = useState(false);
  const [showReactivar, setShowReactivar] = useState(false);
  const [tallerSeleccionado, setTallerSeleccionado] = useState(null);
  const [reactivarForm, setReactivarForm] = useState({
    nivel: 'estandar',
    dias: 30
  });
  const [reactivando, setReactivando] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadTalleresVencer();
  }, [diasFiltro]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [dashRes, vencerRes, graciaRes, suspendidosRes] = await Promise.all([
        suscripcionesAPI.getDashboard(),
        suscripcionesAPI.getPorVencer(diasFiltro),
        suscripcionesAPI.getEnGracia(),
        suscripcionesAPI.getSuspendidos()
      ]);
      setDashboard(dashRes.data);
      setTalleresVencer(vencerRes.data);
      setTalleresEnGracia(graciaRes.data);
      setTalleresSuspendidos(suspendidosRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos de suscripciones');
    } finally {
      setLoading(false);
    }
  };

  const loadTalleresVencer = async () => {
    try {
      const res = await suscripcionesAPI.getPorVencer(diasFiltro);
      setTalleresVencer(res.data);
    } catch (error) {
      console.error('Error loading talleres por vencer:', error);
    }
  };

  const handleEjecutarCron = async () => {
    if (!window.confirm('¿Ejecutar el cron de suscripciones ahora? Esto enviará notificaciones y suspenderá talleres vencidos.')) {
      return;
    }
    
    setEjecutandoCron(true);
    try {
      const res = await suscripcionesAPI.ejecutarCron();
      toast.success('Cron ejecutado exitosamente');
      console.log('Resultado cron:', res.data);
      loadData(); // Recargar datos
    } catch (error) {
      console.error('Error ejecutando cron:', error);
      toast.error('Error al ejecutar el cron');
    } finally {
      setEjecutandoCron(false);
    }
  };

  const handleReactivar = async () => {
    if (!tallerSeleccionado) return;
    
    setReactivando(true);
    try {
      await suscripcionesAPI.reactivarTaller(
        tallerSeleccionado.id,
        reactivarForm.nivel,
        reactivarForm.dias
      );
      toast.success(`Taller reactivado con plan ${reactivarForm.nivel.toUpperCase()} por ${reactivarForm.dias} días`);
      setShowReactivar(false);
      setTallerSeleccionado(null);
      loadData();
    } catch (error) {
      console.error('Error reactivando taller:', error);
      toast.error('Error al reactivar el taller');
    } finally {
      setReactivando(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      // Parsear solo la parte de fecha para evitar problemas de timezone
      const datePart = dateStr.split('T')[0];
      const [year, month, day] = datePart.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString('es-AR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const getBadgeVariant = (diasRestantes) => {
    if (diasRestantes <= 2) return 'destructive';
    if (diasRestantes <= 5) return 'warning';
    return 'secondary';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Calendar className="h-6 w-6 text-teal-600" />
            Gestión de Suscripciones
          </h1>
          <p className="text-gray-500 mt-1">Control de vencimientos y suspensiones automáticas</p>
          <p className="text-xs text-gray-400 mt-1 flex items-center gap-1" data-testid="ultimo-cron-info">
            <Zap className="h-3 w-3 text-purple-400" />
            Último cron: {dashboard?.ultimo_cron?.fecha ? formatDate(dashboard.ultimo_cron.fecha) : 'Nunca'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData} disabled={loading} data-testid="susc-actualizar-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="susc-acciones-avanzadas-btn">
                <MoreVertical className="h-4 w-4 mr-2" />
                Acciones avanzadas
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel>Mantenimiento del sistema</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleEjecutarCron}
                disabled={ejecutandoCron}
                className="cursor-pointer"
                data-testid="susc-ejecutar-cron-btn"
              >
                {ejecutandoCron ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Play className="h-4 w-4 mr-2 text-amber-600" />
                )}
                <div className="flex flex-col">
                  <span>Ejecutar Cron Ahora</span>
                  <span className="text-xs text-slate-500">Fuerza verificación de vencimientos</span>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-100 text-sm">Talleres Activos</p>
                <p className="text-3xl font-bold">{dashboard?.resumen?.total_activos || 0}</p>
              </div>
              <CheckCircle className="h-10 w-10 text-emerald-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-100 text-sm">Por Vencer (7 días)</p>
                <p className="text-3xl font-bold">{dashboard?.proximos_a_vencer?.length || 0}</p>
              </div>
              <Clock className="h-10 w-10 text-amber-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-500 to-rose-600 text-white" data-testid="kpi-en-gracia">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm">En Período de Gracia</p>
                <p className="text-3xl font-bold">{talleresEnGracia.length}</p>
              </div>
              <Hourglass className="h-10 w-10 text-orange-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-sm">Suspendidos</p>
                <p className="text-3xl font-bold">{dashboard?.resumen?.total_suspendidos ?? dashboard?.resumen?.por_estado?.suspendido ?? 0}</p>
              </div>
              <XCircle className="h-10 w-10 text-red-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-4">
        <TabsList>
          <TabsTrigger value="por-vencer" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Por Vencer
            {talleresVencer.length > 0 && (
              <Badge variant="destructive" className="ml-1">{talleresVencer.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="en-gracia" className="flex items-center gap-2" data-testid="tab-en-gracia">
            <Hourglass className="h-4 w-4" />
            En Gracia
            {talleresEnGracia.length > 0 && (
              <Badge className="ml-1 bg-orange-500 text-white hover:bg-orange-500">{talleresEnGracia.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="suspendidos" className="flex items-center gap-2">
            <XCircle className="h-4 w-4" />
            Suspendidos
            {talleresSuspendidos.length > 0 && (
              <Badge variant="secondary" className="ml-1">{talleresSuspendidos.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="notificaciones" className="flex items-center gap-2" data-testid="tab-notificaciones-masivas">
            <Send className="h-4 w-4" />
            Notificaciones Masivas
          </TabsTrigger>
          <TabsTrigger value="ranking" className="flex items-center gap-2" data-testid="tab-ranking">
            <Trophy className="h-4 w-4" />
            Ranking
          </TabsTrigger>
        </TabsList>

        {/* Tab: Por Vencer */}
        <TabsContent value="por-vencer">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-500" />
                    Talleres Próximos a Vencer
                  </CardTitle>
                  <CardDescription>
                    Talleres cuya suscripción vence en los próximos días
                  </CardDescription>
                </div>
                <Select value={diasFiltro.toString()} onValueChange={(v) => setDiasFiltro(parseInt(v))}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">Próximos 3 días</SelectItem>
                    <SelectItem value="7">Próximos 7 días</SelectItem>
                    <SelectItem value="15">Próximos 15 días</SelectItem>
                    <SelectItem value="30">Próximos 30 días</SelectItem>
                    <SelectItem value="60">Próximos 60 días</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {talleresVencer.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="h-12 w-12 text-emerald-400 mx-auto mb-3" />
                  <p className="text-gray-500">No hay talleres próximos a vencer en los próximos {diasFiltro} días</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {talleresVencer.map((taller) => (
                    <div key={taller.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{taller.razon_social}</p>
                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                          <span>ANAC: {taller.numero_anac || 'N/A'}</span>
                          <Badge variant="outline">{taller.nivel_suscripcion?.toUpperCase()}</Badge>
                          {taller.debito_automatico && (
                            <Badge className="bg-emerald-100 text-emerald-700">Débito Auto</Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <div className="text-right">
                          <p className="text-sm text-gray-500">Vence:</p>
                          <p className="font-medium">{formatDate(taller.fecha_vencimiento)}</p>
                          <Badge variant={getBadgeVariant(taller.dias_restantes)}>
                            {taller.dias_restantes} días
                          </Badge>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          title="Enviar mensaje directo a este taller"
                          onClick={() => navigate(`/admin/suscripciones?activeTab=notificaciones&taller_ids=${taller.id}&taller_nombre=${encodeURIComponent(taller.razon_social || '')}`)}
                          data-testid={`enviar-mensaje-${taller.id}`}
                        >
                          <Send className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: En Período de Gracia */}
        <TabsContent value="en-gracia">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Hourglass className="h-5 w-5 text-orange-500" />
                Talleres en Período de Gracia
              </CardTitle>
              <CardDescription>
                Vencidos pero aún no suspendidos — gracia de 10 días (mensual) o 30 días (anual)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {talleresEnGracia.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="h-12 w-12 text-emerald-400 mx-auto mb-3" />
                  <p className="text-gray-500">No hay talleres en período de gracia</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {talleresEnGracia.map((taller) => (
                    <div key={taller.id} className="flex items-center justify-between p-4 border border-orange-200 bg-orange-50/40 rounded-lg hover:bg-orange-50" data-testid={`gracia-taller-${taller.id}`}>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{taller.razon_social}</p>
                        <div className="flex items-center gap-3 mt-1 text-sm text-gray-500 flex-wrap">
                          <span>ANAC: {taller.numero_anac || 'N/A'}</span>
                          <Badge variant="outline">{taller.nivel_suscripcion?.toUpperCase()}</Badge>
                          <Badge className={taller.forma_pago === 'anual' ? 'bg-purple-100 text-purple-700 hover:bg-purple-100' : 'bg-blue-100 text-blue-700 hover:bg-blue-100'}>
                            {taller.forma_pago === 'anual' ? 'Anual' : 'Mensual'}
                          </Badge>
                          {taller.debito_automatico && (
                            <Badge className="bg-emerald-100 text-emerald-700">Débito Auto</Badge>
                          )}
                        </div>
                        <p className="text-xs text-gray-400 mt-1">
                          Venció: {formatDate(taller.fecha_vencimiento)}
                          {taller.dias_vencido != null && ` — hace ${taller.dias_vencido} día${taller.dias_vencido === 1 ? '' : 's'}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <div className="text-right">
                          <p className="text-sm text-gray-500">Gracia restante:</p>
                          <Badge variant={taller.dias_gracia_restantes != null && taller.dias_gracia_restantes <= 3 ? 'destructive' : 'secondary'} className={taller.dias_gracia_restantes != null && taller.dias_gracia_restantes <= 3 ? '' : 'bg-orange-100 text-orange-700 hover:bg-orange-100'}>
                            {taller.dias_gracia_restantes != null ? `${taller.dias_gracia_restantes} de ${taller.dias_gracia_total} días` : 'N/A'}
                          </Badge>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          title="Enviar mensaje directo a este taller"
                          onClick={() => navigate(`/admin/suscripciones?activeTab=notificaciones&taller_ids=${taller.id}&taller_nombre=${encodeURIComponent(taller.razon_social || '')}`)}
                          data-testid={`enviar-mensaje-gracia-${taller.id}`}
                        >
                          <Send className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => {
                            navigate(`/admin/finanzas?tab=pagos&open_suscripcion=1&taller_id=${taller.id}&nivel=${taller.nivel_suscripcion || 'estandar'}`);
                          }}
                          className="bg-emerald-600 hover:bg-emerald-700"
                          data-testid={`registrar-pago-gracia-${taller.id}`}
                          title="Abre el formulario de Suscripción Mensual con este taller pre-seleccionado"
                        >
                          <DollarSign className="h-4 w-4 mr-1" />
                          + Registrar pago
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Suspendidos */}
        <TabsContent value="suspendidos">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <XCircle className="h-5 w-5 text-red-500" />
                Talleres Suspendidos
              </CardTitle>
              <CardDescription>
                Talleres en Status Report por falta de pago
              </CardDescription>
            </CardHeader>
            <CardContent>
              {talleresSuspendidos.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="h-12 w-12 text-emerald-400 mx-auto mb-3" />
                  <p className="text-gray-500">No hay talleres suspendidos</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {talleresSuspendidos.map((taller) => (
                    <div key={taller.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{taller.razon_social}</p>
                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                          <span>ANAC: {taller.numero_anac || 'N/A'}</span>
                          <span>Plan anterior: <strong>{taller.nivel_antes_suspension?.toUpperCase()}</strong></span>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">
                          Suspendido: {formatDate(taller.fecha_suspension)}
                        </p>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          title="Enviar mensaje directo a este taller"
                          onClick={() => navigate(`/admin/suscripciones?activeTab=notificaciones&taller_ids=${taller.id}&taller_nombre=${encodeURIComponent(taller.razon_social || '')}`)}
                          data-testid={`enviar-mensaje-suspendido-${taller.id}`}
                        >
                          <Send className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setTallerSeleccionado(taller);
                            setReactivarForm({
                              nivel: taller.nivel_antes_suspension || 'estandar',
                              dias: 30
                            });
                            setShowReactivar(true);
                          }}
                          className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          data-testid={`reactivar-sin-cobro-${taller.id}`}
                          title="Reactiva sin registrar cobro — útil para regalos o excepciones"
                        >
                          <RotateCw className="h-4 w-4 mr-1" />
                          Sin cobro
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => {
                            // Abrir Finanzas con el formulario manual pre-cargado
                            navigate(`/admin/finanzas?tab=pagos&open_suscripcion=1&taller_id=${taller.id}&nivel=${taller.nivel_antes_suspension || 'estandar'}`);
                          }}
                          className="bg-emerald-600 hover:bg-emerald-700"
                          data-testid={`reactivar-con-cobro-${taller.id}`}
                          title="Abre el formulario de Suscripción Mensual con este taller pre-seleccionado"
                        >
                          <DollarSign className="h-4 w-4 mr-1" />
                          + Registrar pago
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Notificaciones Masivas */}
        <TabsContent value="notificaciones">
          <NotificacionesMasivas />
        </TabsContent>

        {/* Tab: Ranking de Clientes */}
        <TabsContent value="ranking">
          <RankingClientes />
        </TabsContent>
      </Tabs>

      {/* Dialog: Reactivar Taller */}
      <Dialog open={showReactivar} onOpenChange={setShowReactivar}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reactivar Suscripción</DialogTitle>
            <DialogDescription>
              Reactivar manualmente el taller: <strong>{tallerSeleccionado?.razon_social}</strong>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nivel de Suscripción</Label>
              <Select 
                value={reactivarForm.nivel} 
                onValueChange={(v) => setReactivarForm({...reactivarForm, nivel: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="estandar">Estándar ($100 USD/mes)</SelectItem>
                  <SelectItem value="estandar_pro">Estándar Pro ($200 USD/mes)</SelectItem>
                  <SelectItem value="premium">Premium ($350 USD/mes)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Días de Acceso</Label>
              <Input
                type="number"
                min={1}
                max={365}
                value={reactivarForm.dias}
                onChange={(e) => setReactivarForm({...reactivarForm, dias: parseInt(e.target.value) || 30})}
              />
              <p className="text-xs text-gray-500">
                Normalmente 30 días. Ajustar solo en casos especiales.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReactivar(false)}>
              Cancelar
            </Button>
            <Button onClick={handleReactivar} disabled={reactivando}>
              {reactivando ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              Reactivar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSuscripciones;
